import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { META, pickPractice, recordAnswer } from "../lib/store";
import { Setup, Result } from "./Flashcards";
import { ClipboardList, CheckCircle2, XCircle, ChevronRight } from "lucide-react";

export default function Quiz() {
  const { level } = useApp();
  const cfg = META[level];
  const [config, setConfig] = useState(null);
  const [qs, setQs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState(false);
  const [streak, setStreak] = useState(0);

  const start = (section, size) => {
    setQs(pickPractice(level, size, section === "all" ? null : section));
    setConfig({ section, size });
    setIdx(0);
    setSelected(null);
    setCorrect(0);
    setDone(false);
    setStreak(0);
  };

  const choose = (oi) => {
    if (selected !== null) return;
    const q = qs[idx];
    const ok = oi === q.answer;
    setSelected(oi);
    recordAnswer(level, q.id, ok);
    if (ok) {
      setCorrect((c) => c + 1);
      const ns = streak + 1;
      setStreak(ns);
      if (ns >= 3 && ns % 3 === 0) toast.success(`Série de ${ns} bonnes réponses !`);
    } else {
      setStreak(0);
    }
  };

  const next = () => {
    if (idx + 1 >= qs.length) {
      setDone(true);
    } else {
      setIdx((i) => i + 1);
      setSelected(null);
    }
  };

  if (!config)
    return (
      <Setup
        cfg={cfg}
        title="Quiz par thème"
        subtitle="Répondez avec retour immédiat. Idéal pour renforcer une section."
        onStart={start}
        icon={ClipboardList}
        ctaLabel="Lancer le quiz"
      />
    );

  if (done) return <Result known={correct} total={qs.length} onRestart={() => setConfig(null)} label="bonnes réponses" />;

  const q = qs[idx];
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => setConfig(null)} data-testid="quiz-quit" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          Quitter
        </button>
        <div className="flex items-center gap-4 font-mono text-sm">
          <span className="text-success">{correct} bonnes</span>
          <span className="text-muted-foreground">{idx + 1} / {qs.length}</span>
        </div>
      </div>
      <div className="h-1.5 rounded-sm bg-muted overflow-hidden">
        <div className="h-full bg-primary transition-[width]" style={{ width: `${(idx / qs.length) * 100}%` }} />
      </div>

      <div className="rounded-sm border border-border bg-card p-6">
        <span className="font-mono text-xs text-primary">{q.id}</span>
        <h2 className="font-display font-bold text-xl sm:text-2xl mt-2 leading-snug">{q.question}</h2>
      </div>

      <div className="space-y-2.5" data-testid="quiz-options">
        {q.options.map((opt, oi) => {
          const isCorrect = oi === q.answer;
          const isSelected = oi === selected;
          let cls = "border-border hover:border-primary/60 hover:bg-secondary/40";
          let Icon = null;
          if (selected !== null) {
            if (isCorrect) {
              cls = "border-success bg-success/12";
              Icon = CheckCircle2;
            } else if (isSelected) {
              cls = "border-destructive bg-destructive/12";
              Icon = XCircle;
            } else {
              cls = "border-border opacity-60";
            }
          }
          return (
            <motion.button
              key={opt}
              whileTap={{ scale: selected === null ? 0.99 : 1 }}
              onClick={() => choose(oi)}
              disabled={selected !== null}
              data-testid={`quiz-option-${oi}`}
              className={`w-full text-left flex items-center gap-3 rounded-sm border px-4 py-3.5 text-sm transition-colors ${cls}`}
            >
              <span className="font-mono text-xs w-5 shrink-0 text-center">{String.fromCharCode(65 + oi)}</span>
              <span className="flex-1">{opt}</span>
              {Icon && <Icon size={18} className={isCorrect ? "text-success" : "text-destructive"} />}
            </motion.button>
          );
        })}
      </div>

      {selected !== null && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between gap-4">
          <span className={`text-sm font-semibold ${selected === q.answer ? "text-success" : "text-destructive"}`}>
            {selected === q.answer ? "Correct !" : "Incorrect — la bonne réponse est surlignée."}
          </span>
          <button onClick={next} data-testid="quiz-next" className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-5 py-2.5 font-semibold hover:-translate-y-px transition-transform">
            {idx + 1 >= qs.length ? "Terminer" : "Suivant"} <ChevronRight size={18} />
          </button>
        </motion.div>
      )}
    </div>
  );
}
